import type { SimaBand, StratumId } from '@somnhold/content';

/*
 * The Chasm's twelve strata as data for the one crypt material (engine/sima.ts): the floor's and walls' paint, the
 * pattern the shader draws over them (seams, an overlay of moss or silk or paper, plates, facets), their glow, the
 * murk and the light. Every stratum is told apart by one colour that leads (STRATUM[id].color in content), and
 * nothing here is gold or beige: bone is grey, paper is cold grey, wood is dark.
 *
 * All of it goes in as uniforms and vertex colours, never as shader defines: one program for all twelve
 * (docs/RENDIMIENTO.md, «Trampas»: each define is another program, another compile on a phone).
 */

/** Ambient motes (one Points draw, one material): how they move and which cell of the sprite atlas they show. */
export const MOTION = { rise: 0, fall: 1, drift: 2, twinkle: 3, drip: 4, plunge: 5 } as const;
export const CELL = { dot: 0, star: 1, drop: 2, chunk: 3 } as const;

export interface Motes {
  motion: number;
  cell: number;
  color: string;
  /** Brightness (colours over 1 read as glowing after tone mapping). */
  glow: number;
  n: number;
  size: number;
}

export interface StratumLook {
  /** Vertex paint: floor dark → light, grout, wall dark → light (the crypt's own light and shade kept). */
  floor: [string, string];
  grout: string;
  wall: [string, string];
  /** Seams (the shared Voronoi map): scale, width, how patchy (0 everywhere … 1 only where alive), facet tilt. */
  vein: [number, number, number, number];
  /** Seam glow colour, glint colour, and [base glow, twinkle, beat speed, how much the seam repaints the stone]. */
  seam: string;
  glint: string;
  seamA: [number, number, number, number];
  /** What the seam repaints the stone to. */
  seamPaint: string;
  /** Glow in the grout and at the foot of the walls. */
  grout2: [string, number];
  foot: [string, number];
  /** An overlay by noise (moss, silk, rime, ink, verdigris…): colour, [floor, walls, scale, threshold], its glow. */
  over: string;
  overA: [number, number, number, number];
  overGlow: [string, number];
  /** Whole plates of the Voronoi map painted over (paper, iron plates): colour, [threshold, amount, gloss, -]. */
  cell: string;
  cellA: [number, number, number, number];
  /** Roughness (−1: the crypt's), metalness, and the slow beat of the whole place (speed, amount: the ribs). */
  misc: [number, number, number, number];
  /** The murk: background, fog colour, hemisphere sky and ground and its intensity, moonlight and its intensity. */
  bg: string;
  fog: string;
  hemi: [string, string, number];
  moon: [string, number];
  /** Room lights (the fixed ROOM_SLOTS take these): rooms and the hall, colour and intensity. */
  room: [string, number];
  hall: [string, number];
  /** The party's torches lean toward this, the wall flames burn this way (colour, emissive), the hall's runes. */
  torch: string;
  flame: [string, string];
  rune: string;
  motes: Motes[];
}

const L = (o: Partial<StratumLook> & Pick<StratumLook, 'floor' | 'grout' | 'wall' | 'bg' | 'fog' | 'hemi' | 'moon' | 'room' | 'hall' | 'torch' | 'flame' | 'rune' | 'motes'>): StratumLook => ({
  vein: [0.16, 0.012, 1, 0],
  seam: '#000000',
  glint: '#000000',
  seamA: [0, 0, 0, 0.35],
  seamPaint: '#000000',
  grout2: ['#000000', 0],
  foot: ['#000000', 0],
  over: '#000000',
  overA: [0, 0, 0.08, 1.2],
  overGlow: ['#000000', 0],
  cell: '#000000',
  cellA: [2, 0, 0, 0],
  misc: [-1, 0, 0, 0],
  ...o,
});

export const LOOKS: Record<StratumId, StratumLook> = {
  /** Blue-grey slate, cracked; candles and torches, bone dust. */
  ossuary: L({
    floor: ['#232832', '#5c6678'],
    grout: '#0a0b0e',
    wall: ['#12151b', '#3e4554'],
    vein: [0.16, 0.012, 0.7, 0.05],
    seamA: [0, 0, 0, 0.6],
    seamPaint: '#0a0b0e',
    over: '#8a919c',
    overA: [0.22, 0.12, 0.09, 0.64],
    bg: '#06070b',
    fog: '#0a0d14',
    hemi: ['#6f80a4', '#0a0b12', 1.0],
    moon: ['#8fa3c0', 1.0],
    room: ['#9fb4d8', 13],
    hall: ['#b9c8ec', 22],
    torch: '#ffb070',
    flame: ['#ffcf7a', '#ff7a1a'],
    rune: '#8fa3c0',
    motes: [{ motion: MOTION.drift, cell: CELL.dot, color: '#b8c4d8', glow: 0.7, n: 110, size: 0.06 }],
  }),
  /** Basalt split by fire: the crypt in embers. */
  marrow: L({
    floor: ['#141112', '#38302f'],
    grout: '#0a0605',
    wall: ['#090707', '#2a2425'],
    vein: [0.09, 0.03, 0.85, 0],
    seam: '#ff5a14',
    glint: '#ffb060',
    seamA: [0.9, 0.35, 0.9, 0.95],
    seamPaint: '#080202',
    grout2: ['#ff3a0c', 0.08],
    foot: ['#3a0602', 1],
    bg: '#0a0404',
    fog: '#160605',
    hemi: ['#8a6a66', '#2a0c06', 0.9],
    moon: ['#b08a86', 0.6],
    room: ['#ff8a4a', 14],
    hall: ['#ff4a2a', 22],
    torch: '#ff8a4a',
    flame: ['#ffd2a0', '#ff3d12'],
    rune: '#ff4f3a',
    motes: [{ motion: MOTION.rise, cell: CELL.dot, color: '#ff6a24', glow: 2.2, n: 220, size: 0.07 }],
  }),
  /** Black glass flagstones cut in facets, violet seams. */
  glass: L({
    floor: ['#0b0a10', '#2e2b39'],
    grout: '#040307',
    wall: ['#07060a', '#25222e'],
    vein: [0.16, 0.022, 0, 0.7],
    seam: '#9e6bff',
    glint: '#ff4f3a',
    seamA: [0.05, 0.75, 0.4, 0.4],
    seamPaint: '#575270',
    grout2: ['#29104f', 0.1],
    foot: ['#100520', 1],
    misc: [0.3, 0.15, 0, 0],
    bg: '#07060c',
    fog: '#100c1a',
    hemi: ['#7a78a0', '#120a1e', 0.9],
    moon: ['#a69cd8', 0.75],
    room: ['#b9a6ff', 14],
    hall: ['#c9b8ff', 22],
    torch: '#c9b8ff',
    flame: ['#f0ecff', '#9a7aff'],
    rune: '#c9b8ff',
    motes: [{ motion: MOTION.twinkle, cell: CELL.star, color: '#d9ccff', glow: 1.6, n: 120, size: 0.08 }],
  }),
  /** Black water over wet slabs; pale water-green light and dripping. */
  cisterns: L({
    floor: ['#0b1314', '#2b3c3d'],
    grout: '#030606',
    wall: ['#091011', '#2a3c3e'],
    vein: [0.12, 0.01, 1, 0.08],
    seamA: [0, 0, 0, 0.5],
    seamPaint: '#030606',
    over: '#1d4434',
    overA: [0.05, 0.5, 0.07, 0.56],
    foot: ['#0e4a40', 0.5],
    misc: [0.2, 0.1, 0, 0],
    bg: '#030808',
    fog: '#061412',
    hemi: ['#5f9f94', '#02100d', 0.85],
    moon: ['#7fe0cc', 0.8],
    room: ['#6fe8cc', 13],
    hall: ['#5fd0b8', 22],
    torch: '#b8f0dc',
    flame: ['#e6fff6', '#3fd0a8'],
    rune: '#5fd0b8',
    motes: [{ motion: MOTION.drip, cell: CELL.drop, color: '#a8f4e4', glow: 1.1, n: 90, size: 0.09 }],
  }),
  /** Black earth and moss, roots through the ceiling; fireflies. */
  buried: L({
    floor: ['#100c09', '#3a2c20'],
    grout: '#040302',
    wall: ['#0b0806', '#2c2219'],
    vein: [0.2, 0.02, 0.6, 0.15],
    seamA: [0, 0, 0, 0.7],
    seamPaint: '#050302',
    over: '#3c6e24',
    overA: [0.5, 0.6, 0.07, 0.5],
    overGlow: ['#1a3a08', 0.1],
    misc: [0.95, 0, 0, 0],
    bg: '#040704',
    fog: '#0a140a',
    hemi: ['#6f9a52', '#0a0e06', 0.95],
    moon: ['#9fd07a', 0.7],
    room: ['#94dc6a', 13],
    hall: ['#7fc25a', 22],
    torch: '#d8f0a0',
    flame: ['#efffc8', '#7fc25a'],
    rune: '#9fe070',
    motes: [{ motion: MOTION.twinkle, cell: CELL.dot, color: '#c8ff6a', glow: 2.6, n: 70, size: 0.09 }],
  }),
  /** A soft dark floor with glowing patches, giant mushrooms; cyan spores. */
  grotto: L({
    floor: ['#0c0a1a', '#2a2448'],
    grout: '#04030a',
    wall: ['#0a0816', '#262040'],
    vein: [0.18, 0.03, 1, 0.25],
    seamA: [0, 0, 0, 0.45],
    seamPaint: '#05040c',
    over: '#174f66',
    overA: [0.4, 0.32, 0.14, 0.64],
    overGlow: ['#26c8ff', 0.55],
    misc: [0.7, 0, 0, 0],
    bg: '#03050c',
    fog: '#05101a',
    hemi: ['#4f9fc8', '#06061a', 0.85],
    moon: ['#6fd8ff', 0.6],
    room: ['#4fd6ff', 12],
    hall: ['#7fe8ff', 22],
    torch: '#a8f0ff',
    flame: ['#e0faff', '#2fc0ff'],
    rune: '#4fd6ff',
    motes: [{ motion: MOTION.drift, cell: CELL.dot, color: '#5fe0ff', glow: 1.8, n: 170, size: 0.08 }],
  }),
  /** Blue ice, white cracks and rime; fine snow. */
  frost: L({
    floor: ['#1f3654', '#7298c2'],
    grout: '#0a1626',
    wall: ['#111e34', '#4e6f9a'],
    vein: [0.14, 0.016, 0.5, 0.55],
    seam: '#cfe8ff',
    glint: '#ffffff',
    seamA: [0.05, 0.5, 0.6, 0.5],
    seamPaint: '#eaf4ff',
    over: '#e4f0ff',
    overA: [0.2, 0.3, 0.1, 0.62],
    foot: ['#1e4a7a', 0.6],
    misc: [0.16, 0.05, 0, 0],
    bg: '#050910',
    fog: '#0c1624',
    hemi: ['#a8c8ff', '#0a1424', 1.05],
    moon: ['#dceeff', 1.0],
    room: ['#cfe8ff', 13],
    hall: ['#e8f4ff', 22],
    torch: '#dceeff',
    flame: ['#f4faff', '#7ab8ff'],
    rune: '#cfe8ff',
    motes: [{ motion: MOTION.fall, cell: CELL.star, color: '#f4f8ff', glow: 1.3, n: 230, size: 0.07 }],
  }),
  /** Amethyst facets with green and magenta crystal; glints. */
  geode: L({
    floor: ['#1c0e28', '#52306e'],
    grout: '#07030c',
    wall: ['#140a20', '#40255a'],
    vein: [0.2, 0.02, 0, 0.65],
    seam: '#ff6af0',
    glint: '#4aff9a',
    seamA: [0.18, 0.9, 0.7, 0.35],
    seamPaint: '#a070d0',
    over: '#1c5a3a',
    overA: [0.14, 0.22, 0.06, 0.7],
    overGlow: ['#2aff7a', 0.3],
    misc: [0.25, 0.2, 0, 0],
    bg: '#08030c',
    fog: '#160a20',
    hemi: ['#c88ae8', '#1a0624', 1.0],
    moon: ['#e8a8ff', 0.8],
    room: ['#e070ff', 15],
    hall: ['#8affc0', 22],
    torch: '#f0c8ff',
    flame: ['#ffe8ff', '#e050ff'],
    rune: '#7affb0',
    motes: [{ motion: MOTION.twinkle, cell: CELL.star, color: '#ff9af8', glow: 2.2, n: 90, size: 0.09 }, { motion: MOTION.twinkle, cell: CELL.star, color: '#7affb0', glow: 2.2, n: 60, size: 0.08 }],
  }),
  /** Cold grey stone strewn with paper and ink; candles. */
  archive: L({
    floor: ['#17191d', '#474b53'],
    grout: '#060708',
    wall: ['#0f1013', '#363a42'],
    vein: [0.16, 0.01, 1, 0],
    seamA: [0, 0, 0, 0.4],
    seamPaint: '#060708',
    over: '#06050b',
    overA: [0.3, 0.12, 0.05, 0.68],
    overGlow: ['#1a0a30', 0.25],
    cell: '#71767f',
    cellA: [0.8, 0.75, 0, 0],
    misc: [0.82, 0, 0, 0],
    bg: '#05060a',
    fog: '#0c0e12',
    hemi: ['#9aa7b4', '#0a0b0e', 0.9],
    moon: ['#c8d2de', 0.8],
    room: ['#d4dcea', 12],
    hall: ['#e6ecf6', 20],
    torch: '#f0f4fa',
    flame: ['#ffffff', '#c8d8f0'],
    rune: '#ff4f3a',
    motes: [{ motion: MOTION.drift, cell: CELL.chunk, color: '#9aa2ae', glow: 0.8, n: 90, size: 0.07 }],
  }),
  /** Soot and iron plates, verdigris, a low red of old embers; sparks. */
  forge: L({
    floor: ['#0d0b0b', '#33302e'],
    grout: '#030202',
    wall: ['#0a0909', '#2a2624'],
    vein: [0.1, 0.018, 0.8, 0.1],
    seam: '#ff2a0a',
    glint: '#ff8a3a',
    seamA: [0.3, 0.25, 0.5, 0.9],
    seamPaint: '#050202',
    grout2: ['#5a0a04', 0.25],
    over: '#2a6a58',
    overA: [0.22, 0.38, 0.08, 0.6],
    cell: '#2a2725',
    cellA: [0.55, 0.6, 0.55, 0],
    misc: [0.72, 0.35, 0, 0],
    bg: '#070303',
    fog: '#140606',
    hemi: ['#8a5a50', '#140404', 0.8],
    moon: ['#c06a5a', 0.5],
    room: ['#ff5a3a', 12],
    hall: ['#ff3a2a', 20],
    torch: '#ff9a6a',
    flame: ['#ffc0a0', '#ff2a0a'],
    rune: '#ff4f3a',
    motes: [{ motion: MOTION.rise, cell: CELL.drop, color: '#ff7a30', glow: 2.4, n: 110, size: 0.06 }],
  }),
  /** White silk over everything, pale violet light; drifting threads. */
  nest: L({
    floor: ['#2c2838', '#726c88'],
    grout: '#0e0c14',
    wall: ['#1a1622', '#56506c'],
    vein: [0.22, 0.008, 1, 0],
    seam: '#c8b0ff',
    glint: '#ffffff',
    seamA: [0.04, 0.6, 0.8, 0.2],
    seamPaint: '#e8e2f6',
    over: '#e6e2f2',
    overA: [0.5, 0.68, 0.11, 0.46],
    overGlow: ['#a890ff', 0.06],
    misc: [0.92, 0, 0, 0],
    bg: '#07050d',
    fog: '#120e1c',
    hemi: ['#b8a8e0', '#100a1c', 1.0],
    moon: ['#d9c8ff', 0.9],
    room: ['#c8b0ff', 13],
    hall: ['#d9c8ff', 22],
    torch: '#e8dcff',
    flame: ['#f8f2ff', '#b490ff'],
    rune: '#d9c8ff',
    motes: [{ motion: MOTION.drift, cell: CELL.drop, color: '#f2ecff', glow: 1.1, n: 90, size: 0.08 }],
  }),
  /** Dark flesh veined in red that beats slowly, under grey ribs. */
  ribs: L({
    floor: ['#1c0608', '#4e1419'],
    grout: '#070102',
    wall: ['#150406', '#3c1015'],
    vein: [0.12, 0.04, 0.9, 0.12],
    seam: '#ff2236',
    glint: '#ff6a5a',
    seamA: [0.28, 0, 1.2, 0.75],
    seamPaint: '#240205',
    foot: ['#4a0408', 0.7],
    over: '#5e1a24',
    overA: [0.2, 0.2, 0.1, 0.6],
    misc: [0.42, 0, 1.6, 0.3],
    bg: '#070102',
    fog: '#160406',
    hemi: ['#a05058', '#1a0204', 0.9],
    moon: ['#d0464f', 0.6],
    room: ['#e2505c', 12],
    hall: ['#ff3a48', 20],
    torch: '#ff9a8a',
    flame: ['#ffd0c8', '#ff2a3a'],
    rune: '#ff4f3a',
    motes: [{ motion: MOTION.drift, cell: CELL.chunk, color: '#7a2a30', glow: 0.8, n: 70, size: 0.07 }],
  }),
};

/**
 * What a depth band lays over any stratum (docs/SIMA.md, «Las bandas»): [ink drips on the walls, ash on the floor,
 * black glass veins, the void at the edges], the light kept, and its own motes.
 */
export interface BandLook {
  over: [number, number, number, number];
  light: number;
  motes: Motes[];
}
export const BANDS: Record<SimaBand, BandLook> = {
  shadow: { over: [0, 0, 0, 0], light: 1, motes: [] },
  ink: { over: [1, 0, 0, 0], light: 0.95, motes: [] },
  ash: { over: [0, 1, 0, 0], light: 0.72, motes: [{ motion: MOTION.fall, cell: CELL.chunk, color: '#a09c98', glow: 1.0, n: 240, size: 0.08 }] },
  obsidian: { over: [0, 0, 1, 0], light: 0.9, motes: [] },
  abyss: { over: [0.6, 0, 0.6, 1], light: 0.85, motes: [{ motion: MOTION.plunge, cell: CELL.chunk, color: '#5a5654', glow: 1.0, n: 90, size: 0.2 }] },
};
